/**
 * Axios singletons para tax-facturador-frontend.
 *
 * Dos backends:
 *   - api          → taxor backend (APP.apiUrl, default :8400) — JWT bearer
 *   - accountApi   → tax-account   (APP.accountUrl, default :8410) — login + refresh + OAuth
 *
 * Token en memoria (no localStorage por XSS). Refresh vía cookie httpOnly
 * que tax-account planta automáticamente en login + OAuth callback.
 */

import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { APP } from './constants'

export const api = axios.create({
  baseURL: APP.apiUrl,
  timeout: 30_000,
  withCredentials: true,
})

export const accountApi = axios.create({
  baseURL: APP.accountUrl,
  timeout: 30_000,
  withCredentials: true, // necesario para que tax-account plante refresh cookie
})

// ─── Token storage en memoria + JWT helpers ────────────────────────
let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function hasValidAccessToken(): boolean {
  if (!accessToken) return false
  const parts = accessToken.split('.')
  if (parts.length !== 3) return false
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    if (typeof payload?.exp !== 'number') return false
    return payload.exp > Math.floor(Date.now() / 1000)
  } catch {
    return false
  }
}

// ─── Refresh flow ─────────────────────────────────────────────────
//
// tax-account planta una cookie httpOnly con el refresh_token.
// Cuando taxor responde 401, intentamos refrescar UNA vez antes de
// mandar al usuario al login.

let refreshInFlight: Promise<string | null> | null = null

async function tryRefresh(): Promise<string | null> {
  // Coalesce: si ya hay un refresh en curso, espera ese.
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      // POST /api/v1/auth/refresh — la cookie viaja sola con withCredentials.
      const r = await accountApi.post<{
        access_token: string
        refresh_token: string
        expires_in: number
      }>('/api/v1/auth/refresh', {})
      if (r.data?.access_token) {
        setAccessToken(r.data.access_token)
        return r.data.access_token
      }
      return null
    } catch {
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

// ─── Interceptors taxor ───────────────────────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as
      | (AxiosRequestConfig & { _retried?: boolean })
      | undefined
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true
      const newToken = await tryRefresh()
      if (newToken) {
        if (!original.headers) original.headers = {}
        ;(original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`
        return api.request(original)
      }
      // Refresh falló — limpiar y mandar a login.
      setAccessToken(null)
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  },
)

// ─── Interceptors accountApi (idénticos a `api`) ──────────────────
//
// Bug histórico: `accountApi` no llevaba el header Authorization. Los
// endpoints públicos (`/auth/login*`, `/auth/refresh`, OAuth start +
// callback) no lo necesitan, pero los autenticados sí — entre ellos
// `/auth/me`, `/me/profile`, `/me/preferences`, sessions, consents.
// Sin estos interceptors, `useAuthContext` recibía 401 `missing_bearer`
// y el SPA quedaba con `auth.me = undefined` → "Sin organización activa"
// aunque la sesión en DB fuera válida.
//
// Refresh + retry se replica del bloque de `api` con una diferencia:
// excluimos `/auth/refresh` y `/auth/login*` del retry para no entrar
// en loop (esos endpoints son los que ROTAN/EMITEN el token; un 401 ahí
// significa credenciales malas, no JWT expirado).
accountApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

accountApi.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as
      | (AxiosRequestConfig & { _retried?: boolean })
      | undefined
    const url = original?.url ?? ''
    const isAuthFlow =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/oauth/')
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !isAuthFlow
    ) {
      original._retried = true
      const newToken = await tryRefresh()
      if (newToken) {
        if (!original.headers) original.headers = {}
        ;(original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`
        return accountApi.request(original)
      }
      setAccessToken(null)
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  },
)

// ─── Tipos ────────────────────────────────────────────────────────

export type ContractKind = 'terms' | 'mandate'

export interface Contract {
  kind: ContractKind
  version: string
  html: string
  html_hash: string
}

export interface OnboardingStatus {
  terms_up_to_date: boolean
  mandate_up_to_date: boolean
  current_terms_version: string
  current_mandate_version: string
  blocked_reason?: string
}

export interface FacturadorProfile {
  tax_client_id: string
  legal_name: string
  trade_name?: string
  nit_verification_digit: string
  address: string
  city: string
  city_dane_code?: string
  department: string
  department_dane_code?: string
  country: string
  postal_code?: string
  ciiu: string
  ciiu_secondary?: string[]
  fiscal_responsibilities: string[]
  tax_regime: string
  logo_url?: string
  primary_color?: string
  resolution_number: string
  resolution_prefix: string
  resolution_from: number
  resolution_to: number
  resolution_valid_from: string
  resolution_valid_to: string
  resolution_technical_key: string
  software_id?: string
  onboarding_completed: boolean
  onboarding_completed_at?: string
  created_at: string
  updated_at: string
}

export interface ProfileStatus {
  has_profile: boolean
  onboarding_completed: boolean
  legal_name?: string
  resolution_prefix?: string
  resolution_from?: number
  resolution_to?: number
  missing_critical: string[]
}

// Snapshot autoritativo desde RUES (empresas.lexplatform.co).
//
// Esta es la fuente única de verdad para todos los campos que viajan al
// XML UBL como información del emisor. El SPA lo consume al cargar el
// wizard y pinta read-only los campos en `filled_fields`.
//
// El `nit_verification_digit` se calcula con algoritmo DIAN — NO es input
// del usuario. SIEMPRE está en `filled_fields` (junto con `nit` y
// `country`) aunque RUES esté caído.
//
// `source` indica el estado del lookup:
//   - "rues"   : snapshot completo, empresa encontrada en Cámara.
//   - "manual" : NIT no registrado en Cámara — captura manual obligatoria.
//   - "error"  : RUES no respondió — captura manual con disclaimer.
export interface RUESSnapshot {
  tax_client_id: string
  nit: string
  nit_verification_digit: string

  // Identidad.
  legal_name: string
  trade_name: string
  org_juridica: string
  tamano_empresa: string
  rep_legal: string
  email_corporativo: string

  // Ubicación.
  address: string
  department: string
  municipality: string
  country: string

  // CIIU resueltos a códigos.
  ciiu: string
  ciiu_secondary: string[]

  // Whitelist read-only.
  filled_fields: string[]

  // Discriminator del origen.
  source: 'rues' | 'manual' | 'error'

  // ISO timestamp del lookup.
  synced_at: string
}

// Estado de consumo de la resolución DIAN activa.
export interface ResolutionStatusResponse {
  // Eco del profile.
  resolution_number: string
  resolution_prefix: string
  resolution_from: number
  resolution_to: number
  resolution_valid_from: string
  resolution_valid_to: string
  resolution_technical_key: string

  // Métricas derivadas.
  range_size: number
  highest_number_issued: number
  used_count: number
  remaining_count: number
  percent_consumed: number
  next_number: number

  // Vigencia.
  days_until_expiry: number
  is_expired: boolean
  alert_level: 'ok' | 'warning' | 'critical' | 'exhausted'
  alert_reasons: string[]
}

// Payload del wizard onboarding.
export interface UpsertProfileRequest {
  legal_name: string
  trade_name?: string
  nit_verification_digit: string
  address: string
  city: string
  city_dane_code?: string
  department: string
  department_dane_code?: string
  country: string
  postal_code?: string
  ciiu: string
  ciiu_secondary?: string[]
  fiscal_responsibilities: string[]
  tax_regime: string
  logo_url?: string
  primary_color?: string
  resolution_number: string
  resolution_prefix: string
  resolution_from: number
  resolution_to: number
  resolution_valid_from: string // YYYY-MM-DD
  resolution_valid_to: string
  // OPCIONAL desde 2026-07: la clave técnica la gestiona Taxor (la emite
  // DIAN por rango y solo el PT puede consultarla vía GetNumberingRange).
  // Si se omite, el backend la resuelve server-side. El SPA NUNCA la pide.
  resolution_technical_key?: string
  software_id?: string
}

// Rango de numeración DIAN del facturador. La clave técnica viaja
// ENMASCARADA — el valor completo solo vive en backend (entra al CUFE
// al emitir; el usuario jamás la digita).
export interface NumberingRange {
  resolution_number: string
  resolution_date?: string
  prefix: string
  from_number: number
  to_number: number
  valid_date_from: string
  valid_date_to: string
  technical_key_masked: string
  is_active: boolean
}

export interface NumberingRangesResult {
  tax_client_id: string
  issuer_nit: string
  // "habilitacion" = resolución de prueba del SIE DIAN; "dian" = consulta
  // GetNumberingRange real como PT.
  source: 'habilitacion' | 'dian'
  ranges: NumberingRange[]
  synced_at: string
}

export interface SyncRangesResponse {
  profile: FacturadorProfile
  ranges: NumberingRangesResult
}

export interface CertResponse {
  has_byo_cert: boolean
  cert?: {
    id: string
    serial_hex: string
    subject_cn: string
    subject_nit?: string
    issuer_cn: string
    issuer_normalized: string
    valid_from: string
    valid_to: string
    days_until_expiration: number
    key_algorithm: string
    status: string
  }
  message?: string
}

// ─── API methods ──────────────────────────────────────────────────

export const onboardingApi = {
  getContract: (kind: ContractKind) =>
    api.get<Contract>(`/api/v1/onboarding/contracts/${kind}`).then((r) => r.data),

  acceptContract: (kind: ContractKind) =>
    api
      .post<{ acceptance_id: string; contract_version: string; accepted_at: string }>(
        '/api/v1/admin/onboarding/accept',
        { kind },
      )
      .then((r) => r.data),

  status: () =>
    api.get<OnboardingStatus>('/api/v1/admin/onboarding/status').then((r) => r.data),
}

export const profileApi = {
  get: (clientId: string) =>
    api
      .get<FacturadorProfile>(`/api/v1/admin/tax-clients/${clientId}/facturador-profile`)
      .then((r) => r.data),

  status: (clientId: string) =>
    api
      .get<ProfileStatus>(`/api/v1/admin/tax-clients/${clientId}/facturador-profile/status`)
      .then((r) => r.data),

  // Snapshot autoritativo desde RUES + DV calculado. Es la fuente
  // principal del wizard: TODOS los campos de identidad, ubicación,
  // rep legal y CIIU vienen de aquí. El SPA pinta disabled los
  // `filled_fields` y muestra banner según `source`.
  fromRues: (clientId: string) =>
    api
      .get<RUESSnapshot>(`/api/v1/admin/tax-clients/${clientId}/facturador-profile/from-rues`)
      .then((r) => r.data),

  upsert: (clientId: string, body: UpsertProfileRequest) =>
    api
      .put<FacturadorProfile>(
        `/api/v1/admin/tax-clients/${clientId}/facturador-profile`,
        body,
      )
      .then((r) => r.data),

  completeOnboarding: (clientId: string) =>
    api
      .post<{ tax_client_id: string; onboarding_completed: boolean; message: string }>(
        `/api/v1/admin/tax-clients/${clientId}/facturador-profile/complete-onboarding`,
      )
      .then((r) => r.data),

  // Rangos de numeración DIAN del facturador (clave técnica enmascarada).
  // En hab devuelve la resolución de prueba del SIE; en prod consulta
  // GetNumberingRange a DIAN vía Taxor como PT.
  numberingRanges: (clientId: string) =>
    api
      .get<NumberingRangesResult>(
        `/api/v1/admin/tax-clients/${clientId}/numbering-ranges`,
      )
      .then((r) => r.data),

  // Sincroniza la resolución activa del profile desde DIAN (elige el
  // rango vigente con mayor vida útil e inyecta la clave técnica
  // server-side).
  syncRanges: (clientId: string) =>
    api
      .post<SyncRangesResponse>(
        `/api/v1/admin/tax-clients/${clientId}/facturador-profile/sync-ranges`,
      )
      .then((r) => r.data),

  // Estado de consumo de la resolución DIAN activa.
  resolutionStatus: (clientId: string) =>
    api
      .get<ResolutionStatusResponse>(
        `/api/v1/admin/tax-clients/${clientId}/facturador-profile/resolution-status`,
      )
      .then((r) => r.data),

  // Upload logo del cliente (multipart). Persiste al storage backend
  // (local hoy, S3 cuando se active) y actualiza
  // facturador_profiles.logo_url. Retorna la URL pública resultante.
  uploadLogo: (clientId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api
      .post<{ logo_url: string; profile_updated: boolean; key?: string }>(
        `/api/v1/admin/tax-clients/${clientId}/branding/logo`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data)
  },
}

export const certApi = {
  get: (clientId: string) =>
    api.get<CertResponse>(`/api/v1/admin/tax-clients/${clientId}/cert`).then((r) => r.data),

  upload: (clientId: string, file: File, password: string, expectedNIT?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('password', password)
    if (expectedNIT) fd.append('expected_nit', expectedNIT)
    return api
      .post(`/api/v1/admin/tax-clients/${clientId}/cert/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },

  archive: (clientId: string, reason: string) =>
    api
      .delete(`/api/v1/admin/tax-clients/${clientId}/cert`, { data: { reason } })
      .then((r) => r.data),
}

// ─── Catálogos ──────────────────────────────────────────────────────

export interface Customer {
  id: string
  tax_client_id: string
  identification_type: string
  identification_number: string
  verification_digit?: string
  legal_name: string
  trade_name?: string
  address: string
  city: string
  city_dane_code?: string
  department: string
  department_dane_code?: string
  country: string
  postal_code?: string
  email?: string // populated only on Get (descifrado)
  phone?: string
  fiscal_responsibilities: string[]
  tax_regime?: string
  is_active: boolean
  archived_at?: string
  created_at: string
  updated_at: string
}

export interface CustomerInput {
  identification_type: string
  identification_number: string
  verification_digit?: string
  legal_name: string
  trade_name?: string
  address: string
  city: string
  city_dane_code?: string
  department: string
  department_dane_code?: string
  country?: string
  postal_code?: string
  email?: string
  phone?: string
  fiscal_responsibilities?: string[]
  tax_regime?: string
}

export interface ListResult<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

export const customerApi = {
  list: (
    clientId: string,
    params: { search?: string; only_active?: boolean; limit?: number; offset?: number } = {},
  ) => {
    const q = new URLSearchParams()
    if (params.search) q.set('search', params.search)
    if (params.only_active === false) q.set('only_active', 'false')
    if (params.limit) q.set('limit', String(params.limit))
    if (params.offset) q.set('offset', String(params.offset))
    return api
      .get<ListResult<Customer>>(`/api/v1/admin/tax-clients/${clientId}/customers?${q}`)
      .then((r) => r.data)
  },

  get: (clientId: string, id: string) =>
    api
      .get<Customer>(`/api/v1/admin/tax-clients/${clientId}/customers/${id}`)
      .then((r) => r.data),

  create: (clientId: string, body: CustomerInput) =>
    api
      .post<Customer>(`/api/v1/admin/tax-clients/${clientId}/customers`, body)
      .then((r) => r.data),

  update: (clientId: string, id: string, body: CustomerInput) =>
    api
      .put<Customer>(`/api/v1/admin/tax-clients/${clientId}/customers/${id}`, body)
      .then((r) => r.data),

  archive: (clientId: string, id: string) =>
    api.delete(`/api/v1/admin/tax-clients/${clientId}/customers/${id}`).then((r) => r.data),
}

export interface Product {
  id: string
  tax_client_id: string
  sku?: string
  name: string
  description?: string
  unit_price: string
  currency: string
  unit_code: string
  tax_iva_percent: string
  tax_iva_excluded: boolean
  tax_inc_percent: string
  standard_code?: string
  standard_code_scheme?: string
  is_active: boolean
  archived_at?: string
  created_at: string
  updated_at: string
}

export interface ProductInput {
  sku?: string
  name: string
  description?: string
  unit_price: string
  currency?: string
  unit_code?: string
  tax_iva_percent?: string
  tax_iva_excluded?: boolean
  tax_inc_percent?: string
  standard_code?: string
  standard_code_scheme?: string
}

// ─── Facturación ────────────────────────────────────────────────────

export type InvoiceDocType = 'invoice' | 'credit_note' | 'debit_note'
export type InvoiceStatus = 'draft' | 'submitting' | 'accepted' | 'rejected' | 'failed'

export interface ManualInvoiceLine {
  id: string
  invoice_id: string
  line_number: number
  product_id?: string
  product_sku?: string
  description: string
  quantity: string
  unit_code: string
  unit_price: string
  line_discount: string
  line_subtotal: string
  tax_iva_percent: string
  tax_iva_amount: string
  tax_iva_excluded: boolean
  tax_inc_percent: string
  tax_inc_amount: string
  line_total: string
  standard_code?: string
  standard_code_scheme?: string
}

export interface ManualInvoice {
  id: string
  tax_client_id: string
  doc_type: InvoiceDocType
  doc_number: string
  cufe?: string
  customer_id?: string
  customer_id_type: string
  customer_id_number: string
  customer_verification_digit?: string
  customer_legal_name: string
  customer_address: string
  customer_city: string
  customer_department: string
  customer_country: string
  customer_email?: string
  customer_phone?: string
  customer_fiscal_responsibilities: string[]
  customer_tax_regime?: string
  issue_date: string
  issue_time: string
  due_date?: string
  payment_means: string
  currency: string
  exchange_rate?: string
  // Anticipo pre-factura (AT v1.9 FBD).
  prepaid_id?: string
  prepaid_amount?: string
  prepaid_received_date?: string
  prepaid_paid_date?: string
  subtotal: string
  total_discount: string
  tax_iva_total: string
  tax_inc_total: string
  tax_ica_total: string
  withholding_iva: string
  withholding_ica: string
  withholding_fte: string
  grand_total: string
  referenced_invoice_id?: string
  referenced_invoice_number?: string
  referenced_invoice_cufe?: string
  referenced_invoice_date?: string
  discrepancy_reason?: string
  discrepancy_code?: string
  status: InvoiceStatus
  status_message?: string
  track_id?: string
  submitted_at?: string
  accepted_at?: string
  rejected_at?: string
  notes?: string
  archived_at?: string
  created_at: string
  updated_at: string
  lines?: ManualInvoiceLine[]
}

export interface CreateInvoiceLineRequest {
  product_id?: string
  description?: string
  quantity: string
  unit_code?: string
  unit_price?: string
  line_discount?: string
  tax_iva_percent?: string
  tax_iva_excluded?: boolean
  tax_inc_percent?: string
}

export interface CreateInvoiceRequest {
  doc_type: InvoiceDocType
  customer_id?: string
  customer_inline?: {
    id_type: string
    id_number: string
    verification_digit?: string
    legal_name: string
    address: string
    city: string
    department: string
    country?: string
  }
  issue_date?: string
  issue_time?: string
  due_date?: string
  payment_means?: string
  currency?: string
  exchange_rate?: string
  lines: CreateInvoiceLineRequest[]
  referenced_invoice_id?: string
  discrepancy_reason?: string
  discrepancy_code?: string
  notes?: string
}

export interface InvoiceSummary {
  from: string
  to: string
  totals: {
    sales_gross_invoice: string
    credit_note_total: string
    debit_note_total: string
    sales_net: string
    tax_iva_total: string
    tax_inc_total: string
    doc_count: number
  }
  by_status: {
    draft: number
    submitting: number
    accepted: number
    rejected: number
    failed: number
  }
  by_doc_type: {
    invoice: number
    credit_note: number
    debit_note: number
  }
}

export const invoiceApi = {
  list: (
    clientId: string,
    params: {
      doc_type?: InvoiceDocType
      status?: InvoiceStatus
      customer_id?: string
      from?: string
      to?: string
      include_archived?: boolean
      limit?: number
      offset?: number
    } = {},
  ) => {
    const q = new URLSearchParams()
    if (params.doc_type) q.set('doc_type', params.doc_type)
    if (params.status) q.set('status', params.status)
    if (params.customer_id) q.set('customer_id', params.customer_id)
    if (params.from) q.set('from', params.from)
    if (params.to) q.set('to', params.to)
    if (params.include_archived) q.set('include_archived', 'true')
    if (params.limit) q.set('limit', String(params.limit))
    if (params.offset) q.set('offset', String(params.offset))
    return api
      .get<ListResult<ManualInvoice>>(`/api/v1/admin/tax-clients/${clientId}/manual-invoices?${q}`)
      .then((r) => r.data)
  },

  get: (clientId: string, id: string) =>
    api
      .get<ManualInvoice>(`/api/v1/admin/tax-clients/${clientId}/manual-invoices/${id}`)
      .then((r) => r.data),

  create: (clientId: string, body: CreateInvoiceRequest) =>
    api
      .post<ManualInvoice>(`/api/v1/admin/tax-clients/${clientId}/manual-invoices`, body)
      .then((r) => r.data),

  archive: (clientId: string, id: string, reason: string) =>
    api
      .delete(`/api/v1/admin/tax-clients/${clientId}/manual-invoices/${id}`, {
        data: { reason },
      })
      .then((r) => r.data),

  // Disparar transmisión a DIAN del orquestador end-to-end.
  submitToDIAN: (clientId: string, id: string) =>
    api
      .post<{
        invoice_id: string
        cufe?: string
        track_id?: string
        accepted: boolean
        status_before: string
        status_after: string
        async: boolean
        message: string
      }>(`/api/v1/admin/tax-clients/${clientId}/manual-invoices/${id}/submit-to-dian`)
      .then((r) => r.data),

  summary: (clientId: string, from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    return api
      .get<InvoiceSummary>(
        `/api/v1/admin/tax-clients/${clientId}/manual-invoices/summary?${q}`,
      )
      .then((r) => r.data)
  },

  // Descargas de artefactos legales (stream binario). El backend valida
  // el client_id contra la org del JWT (guard IDOR).
  downloadSignedXML: (clientId: string, id: string) =>
    api
      .get(`/api/v1/admin/tax-clients/${clientId}/manual-invoices/${id}/signed-xml`, {
        responseType: 'blob',
      })
      .then((r) => r.data as Blob),

  downloadApplicationResponse: (clientId: string, id: string) =>
    api
      .get(`/api/v1/admin/tax-clients/${clientId}/manual-invoices/${id}/application-response`, {
        responseType: 'blob',
      })
      .then((r) => r.data as Blob),
}

export const productApi = {
  list: (
    clientId: string,
    params: { search?: string; sku?: string; only_active?: boolean; limit?: number; offset?: number } = {},
  ) => {
    const q = new URLSearchParams()
    if (params.search) q.set('search', params.search)
    if (params.sku) q.set('sku', params.sku)
    if (params.only_active === false) q.set('only_active', 'false')
    if (params.limit) q.set('limit', String(params.limit))
    if (params.offset) q.set('offset', String(params.offset))
    return api
      .get<ListResult<Product>>(`/api/v1/admin/tax-clients/${clientId}/products?${q}`)
      .then((r) => r.data)
  },

  get: (clientId: string, id: string) =>
    api.get<Product>(`/api/v1/admin/tax-clients/${clientId}/products/${id}`).then((r) => r.data),

  create: (clientId: string, body: ProductInput) =>
    api.post<Product>(`/api/v1/admin/tax-clients/${clientId}/products`, body).then((r) => r.data),

  update: (clientId: string, id: string, body: ProductInput) =>
    api
      .put<Product>(`/api/v1/admin/tax-clients/${clientId}/products/${id}`, body)
      .then((r) => r.data),

  archive: (clientId: string, id: string) =>
    api.delete(`/api/v1/admin/tax-clients/${clientId}/products/${id}`).then((r) => r.data),
}

// ─── Habilitación DIAN ──────────────────────────────────────────────

export type HabilitacionNextAction =
  | 'create_invoices'
  | 'submit_to_dian'
  | 'create_credit_note'
  | 'submit_credit_note'
  | 'create_debit_note'
  | 'submit_debit_note'
  | 'all_done'

// Proyección liviana de una manual_invoice del set (tabla del cockpit).
// El detalle completo (lines, snapshots) vive en invoiceApi.get.
export interface HabilitacionDocument {
  id: string
  doc_type: InvoiceDocType
  doc_number: string
  status: InvoiceStatus
  status_message?: string
  cufe?: string
  track_id?: string
  created_at: string
  submitted_at?: string
  accepted_at?: string
  rejected_at?: string
}

export interface HabilitacionStatus {
  started: boolean
  invoices_created: number
  invoices_accepted: number
  invoices_rejected: number
  credit_note_ready: boolean
  credit_note_id?: string
  credit_note_accepted: boolean
  debit_note_ready: boolean
  debit_note_id?: string
  debit_note_accepted: boolean
  complete: boolean
  next_action: HabilitacionNextAction

  // Agregados cockpit (2026-07, backward-compatible).
  invoices_draft: number
  invoices_submitting: number
  invoices_failed: number
  // Facturas con submitted_at (llegaron a DIAN al menos una vez).
  invoices_transmitted: number
  documents: HabilitacionDocument[]

  // Estado del transmisor DIAN (cert ECD + SOAP). false → el submit
  // retornaría 503; la UI muestra banner con transmitter_message.
  transmitter_ready: boolean
  transmitter_message?: string
}

export const habilitacionApi = {
  status: (clientId: string) =>
    api
      .get<HabilitacionStatus>(`/api/v1/admin/tax-clients/${clientId}/habilitacion`)
      .then((r) => r.data),

  createInitialInvoices: (clientId: string) =>
    api
      .post<{
        created: number
        invoice_ids: string[]
        next_action: string
        message: string
      }>(`/api/v1/admin/tax-clients/${clientId}/habilitacion/invoices`)
      .then((r) => r.data),

  createCreditNote: (clientId: string) =>
    api
      .post<{ credit_note_id: string; next_action: string }>(
        `/api/v1/admin/tax-clients/${clientId}/habilitacion/credit-note`,
      )
      .then((r) => r.data),

  createDebitNote: (clientId: string) =>
    api
      .post<{ debit_note_id: string; next_action: string }>(
        `/api/v1/admin/tax-clients/${clientId}/habilitacion/debit-note`,
      )
      .then((r) => r.data),
}

// ─── Auth ───────────────────────────────────────────────────────────

export type OAuthProvider = 'google' | 'microsoft'

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  person_id: string
  email: string
  first_name: string
  last_name: string
  verification_level: number

  // Cuando MFA es requerido, access/refresh van vacíos y mfa_required=true.
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number

  organization_id?: string | null
  organization_role?: string | null
  razon_social?: string | null

  mfa_required: boolean
  mfa_challenge_token?: string | null
  mfa_challenge_expires_in?: number | null
}

export interface LoginTotpRequest {
  mfa_challenge_token: string
  code: string
}

export interface MeResponse {
  person_id: string
  email: string
  first_name: string
  last_name: string
  active_org_id?: string | null
  active_org_role?: string | null
  verification_level: number
}

export const authApi = {
  // Email + password.
  login: (body: LoginRequest) =>
    accountApi.post<LoginResponse>('/api/v1/auth/login', body).then((r) => r.data),

  // Segundo factor TOTP.
  loginTotp: (body: LoginTotpRequest) =>
    accountApi.post<LoginResponse>('/api/v1/auth/login/totp', body).then((r) => r.data),

  // Silent refresh — usa la cookie httpOnly plantada en login/oauth callback.
  refresh: () =>
    accountApi
      .post<{ access_token: string; refresh_token: string; expires_in: number }>(
        '/api/v1/auth/refresh',
        {},
      )
      .then((r) => r.data),

  // Revoca la sesión actual y limpia la cookie.
  logout: () =>
    accountApi.post('/api/v1/auth/logout', {}).catch(() => {
      /* swallow — el frontend va a limpiar memoria igualmente */
    }),

  me: () => accountApi.get<MeResponse>('/api/v1/auth/me').then((r) => r.data),

  // Construye URL de inicio OAuth para redirect (no es fetch).
  oauthStartUrl: (provider: OAuthProvider, intent: 'login' | 'register' = 'login') =>
    `${APP.accountUrl.replace(/\/$/, '')}/api/v1/auth/oauth/${provider}/start?intent=${intent}`,

  // Self-service registro de empresa para users sin org. Backend crea
  // Organization + Membership(OWNER) + REVOCA la sesión actual + emite
  // nuevo JWT con active_org_id. El caller debe llamar
  // setAccessToken(resp.access_token) inmediatamente.
  registerOrganization: (body: RegisterOrgRequest) =>
    accountApi
      .post<RegisterOrgResponse>('/api/v1/auth/me/register-organization', body)
      .then((r) => r.data),

  // Switch entre orgs del person. Reissue JWT.
  switchOrg: (body: { organization_id: string }) =>
    accountApi
      .post<{
        access_token: string
        refresh_token: string
        token_type: string
        expires_in: number
        active_org_id: string
        active_org_role: string
        razon_social: string | null
      }>('/api/v1/auth/switch-org', body)
      .then((r) => r.data),

  // Sesiones activas + revocación remota.
  listSessions: () =>
    accountApi
      .get<{ sessions: SessionInfo[] }>('/api/v1/auth/sessions')
      .then((r) => r.data),

  revokeSession: (sessionId: string) =>
    accountApi
      .delete(`/api/v1/auth/sessions/${sessionId}`)
      .then((r) => r.data),

  revokeOtherSessions: () =>
    accountApi
      .delete('/api/v1/auth/sessions')
      .then((r) => r.data),
}

export interface SessionInfo {
  session_id: string
  created_at: string
  last_seen_at: string | null
  ip_address: string | null
  user_agent: string | null
  device_fingerprint: string | null
  is_current: boolean
  status: string
}

// ─── Cuenta personal (/me/*) ─────────────────────────────────────────

export interface MyProfile {
  person_id: string
  email: string
  email_verified: boolean
  phone: string | null
  phone_country_code: string | null
  phone_verified: boolean
  first_name: string
  last_name: string
  full_name: string
  initials: string
  avatar_url: string | null
  country_iso: string
  preferred_locale: string | null
  preferred_timezone: string | null
  verification_level: number
  totp_enabled: boolean
}

export interface UpdateProfileRequest {
  first_name?: string | null
  last_name?: string | null
}

export interface UpdatePreferencesRequest {
  preferred_locale?: string | null
  preferred_timezone?: string | null
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export interface SetPasswordRequest {
  new_password: string
  totp_code?: string | null
}

export interface ChangePasswordResponse {
  ok: boolean
  other_sessions_revoked: number
  message: string
}

export interface EmailChangeStartRequest {
  new_email: string
  password?: string | null
  totp_code?: string | null
}

export interface EmailChangeStartResponse {
  ok: boolean
  new_email_masked: string
  expires_in: number
  message: string
}

export interface ChangePhoneRequest {
  phone?: string | null
  phone_country_code?: string | null
  password?: string | null
  totp_code?: string | null
}

export interface OAuthAccountInfo {
  provider: string
  provider_email: string | null
  linked_at: string
  last_login_at: string | null
}

export interface DeleteAccountRequest {
  password: string
  totp_code?: string | null
  confirmation: string // 'ELIMINAR MI CUENTA'
  reason?: string | null
}

export interface LoginHistoryItem {
  occurred_at: string
  event_type: string
  ip_address: string | null
  user_agent: string | null
  success: boolean
}

export const meApi = {
  // ─── Profile ──────────────────────────────────────────────────────
  profile: () =>
    accountApi.get<MyProfile>('/api/v1/me/profile').then((r) => r.data),

  updateProfile: (body: UpdateProfileRequest) =>
    accountApi.patch<MyProfile>('/api/v1/me/profile', body).then((r) => r.data),

  updatePreferences: (body: UpdatePreferencesRequest) =>
    accountApi.patch<MyProfile>('/api/v1/me/preferences', body).then((r) => r.data),

  // ─── Password ─────────────────────────────────────────────────────
  changePassword: (body: ChangePasswordRequest) =>
    accountApi
      .post<ChangePasswordResponse>('/api/v1/me/password', body)
      .then((r) => r.data),

  setPassword: (body: SetPasswordRequest) =>
    accountApi
      .post<ChangePasswordResponse>('/api/v1/me/password/set', body)
      .then((r) => r.data),

  // ─── Email change ────────────────────────────────────────────────
  emailChangeStart: (body: EmailChangeStartRequest) =>
    accountApi
      .post<EmailChangeStartResponse>('/api/v1/me/email/change/start', body)
      .then((r) => r.data),

  emailChangeVerify: (code: string) =>
    accountApi
      .post<{ ok: boolean; email: string; message: string }>(
        '/api/v1/me/email/change/verify',
        { code },
      )
      .then((r) => r.data),

  // ─── Phone ────────────────────────────────────────────────────────
  updatePhone: (body: ChangePhoneRequest) =>
    accountApi.post<MyProfile>('/api/v1/me/phone', body).then((r) => r.data),

  // ─── OAuth linking ───────────────────────────────────────────────
  listOAuthAccounts: () =>
    accountApi
      .get<{ accounts: OAuthAccountInfo[] }>('/api/v1/me/oauth-accounts')
      .then((r) => r.data),

  unlinkOAuthAccount: (provider: string) =>
    accountApi
      .delete(`/api/v1/me/oauth-accounts/${provider}`)
      .then((r) => r.data),

  // ─── Login history ───────────────────────────────────────────────
  loginHistory: () =>
    accountApi
      .get<{ items: LoginHistoryItem[] }>('/api/v1/me/login-history')
      .then((r) => r.data),

  // ─── Privacy (Ley 1581) ───────────────────────────────────────────
  dataExport: () =>
    accountApi
      .get<Blob>('/api/v1/me/data-export', { responseType: 'blob' })
      .then((r) => r.data),

  deleteAccount: (body: DeleteAccountRequest) =>
    accountApi
      .post<{ ok: boolean; deactivated_at: string; grace_period_days: number; message: string }>(
        '/api/v1/me/account/delete',
        body,
      )
      .then((r) => r.data),
}

// ─── MFA TOTP self-service ───────────────────────────────────────────

export interface TotpSetupResponse {
  secret_b32: string
  otpauth_uri: string
  qr_svg: string
  expires_in: number
}

export interface TotpVerifyResponse {
  enabled: boolean
  backup_codes: string[]
}

export interface TotpStatusResponse {
  enabled: boolean
  pending_setup: boolean
}

export const mfaApi = {
  setup: () =>
    accountApi
      .post<TotpSetupResponse>('/api/v1/auth/totp/setup', {})
      .then((r) => r.data),

  verify: (code: string) =>
    accountApi
      .post<TotpVerifyResponse>('/api/v1/auth/totp/verify', { code })
      .then((r) => r.data),

  disable: (password: string, code: string) =>
    accountApi
      .post('/api/v1/auth/totp/disable', { password, code })
      .then((r) => r.data),

  status: () =>
    accountApi
      .get<TotpStatusResponse>('/api/v1/auth/totp/status')
      .then((r) => r.data),
}

// ─── Legal / consents ────────────────────────────────────────────────

export interface LegalDocInfo {
  kind: string
  version: string
  effective_from: string
  requires_reacceptance: boolean
}

export interface CurrentLegalResponse {
  documents: LegalDocInfo[]
}

export interface ConsentInfo {
  kind: string
  version: string
  accepted_at: string
  is_current: boolean
}

export const legalApi = {
  current: () =>
    accountApi
      .get<CurrentLegalResponse>('/api/v1/legal/current')
      .then((r) => r.data),

  myConsents: () =>
    accountApi
      .get<{ consents: ConsentInfo[] }>('/api/v1/me/consents')
      .then((r) => r.data),

  // Backend usa siempre la versión actual del documento — no se manda
  // version en el body, solo kind.
  acceptConsent: (kind: string) =>
    accountApi
      .post('/api/v1/me/consents/accept', { kind })
      .then((r) => r.data),

  revokeConsent: (kind: string, reason?: string) =>
    accountApi
      .post('/api/v1/me/consents/revoke', { kind, reason })
      .then((r) => r.data),
}

// ─── RUES NIT lookup ─────────────────────────────────────────────────

export interface NitLookupResponse {
  found: boolean
  nit: string
  razon_social: string | null
  email_corporativo: string | null
  direccion: string | null
  departamento: string | null
  municipio: string | null
  organizacion_juridica: string | null
  tamano_empresa: string | null
  actividades_economicas: string[] | null
  rep_legal: string | null
}

export const lookupApi = {
  nit: (nit: string) =>
    accountApi
      .get<NitLookupResponse>(`/api/v1/lookup/nit/${encodeURIComponent(nit)}`)
      .then((r) => r.data),
}

export interface RegisterOrgRequest {
  tax_id: string
  tax_id_dv?: string
  razon_social: string
  nombre_comercial?: string
  email_corporativo?: string
}

export interface RegisterOrgResponse {
  organization_id: string
  tax_id: string
  razon_social: string
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

/**
 * Parsea el URL fragment del callback OAuth.
 *
 * tax-account redirige aquí con tres shapes distintos:
 *
 * 1. Login OK:
 *    #access_token=…&refresh_token=…&expires_in=900&person_id=…&active_org_id=…
 *
 * 2. MFA requerido (user con TOTP enrolled, OAuth solo cubre 1er factor):
 *    #mfa_required=1&mfa_challenge_token=…&mfa_challenge_expires_in=300&email=…
 *
 * 3. Error del proveedor o del backend:
 *    #error=oauth_error  (o oauth_state_mismatch, oauth_provider_error, etc.)
 *
 * Retorna null si el hash es vacío. Caller debe distinguir los tres shapes
 * vía `parsed.mfa_challenge_token`, `parsed.error`, `parsed.access_token`.
 */
export function parseOAuthCallbackHash(hash: string): {
  access_token?: string
  refresh_token?: string
  expires_in?: number
  person_id?: string
  active_org_id?: string
  error?: string
  mfa_required?: string
  mfa_challenge_token?: string
  mfa_challenge_expires_in?: number
  email?: string
} | null {
  const h = hash.startsWith('#') ? hash.slice(1) : hash
  if (!h) return null
  const params = new URLSearchParams(h)
  const out: Record<string, string | number> = {}
  params.forEach((v, k) => {
    if (k === 'expires_in' || k === 'mfa_challenge_expires_in') {
      out[k] = parseInt(v, 10) || 0
    } else {
      out[k] = v
    }
  })
  return out
}
